import { Alert, Box, Heading, HStack, Link, Text, Tooltip, VStack } from '@televet/kibble-ui';
import { format } from 'date-fns';
import partial from 'lodash-es/partial';
import useTaskVisualizer from 'pages/Tasks/hooks/useTaskVisualizer';
import React, { useMemo } from 'react';
import { ClinicAvatar } from 'shared/components/Avatars';
import PetParentAvatar from 'shared/components/Avatars/PetParentAvatar';
import { PopoverPlacement } from 'shared/components/Popover';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import {
  ChannelMessageAttachmentEntityType,
  ChannelMessageAttachmentType,
  ChannelViewChannelMessageFragment,
  useGetClinicWidgetRequestQuery,
  useSubscribeToClinicWidgetRequestSubscription,
} from 'shared/types/graphql';
import { selectPrimaryPhoneNumberBestGuess } from 'shared/utils';
import { getClinicWidgetRequestTypeDisplayName } from 'shared/utils/widgetRequests';
import { WidgetRequestAnswerSummary } from './WidgetRequestAnswerSummary';

interface IWidgetRequestMessageProps {
  message: ChannelViewChannelMessageFragment;
}

const WidgetRequestMessage = ({ message }: IWidgetRequestMessageProps): JSX.Element => {
  const attachment = message.attachments?.find(
    (a) => a.entityType === ChannelMessageAttachmentEntityType.ClinicWidgetRequest,
  );
  const clinicWidgetRequestId = attachment?.entityId;
  const isVerificationRequired = attachment?.attachmentType === ChannelMessageAttachmentType.WidgetRequest;

  const { data: widgetRequestData } = useGetClinicWidgetRequestQuery({
    variables: { where: { id: clinicWidgetRequestId } },
  });

  useSubscribeToClinicWidgetRequestSubscription({
    variables: { where: { node: { id: { equals: clinicWidgetRequestId || '' } } } },
    skip:
      !isVerificationRequired ||
      !widgetRequestData?.clinicWidgetRequest ||
      widgetRequestData?.clinicWidgetRequest?.isVerified ||
      !clinicWidgetRequestId,
  });

  const requestType = widgetRequestData?.clinicWidgetRequest?.clinicWidgetRequestType;
  const requestTypeName = requestType ? getClinicWidgetRequestTypeDisplayName(requestType) : 'Widget Request';

  const { viewClinicPetParent } = useSidePanelSearch();
  const handleViewProfileClick = async (clinicPetParentId: string): Promise<void> => {
    viewClinicPetParent({ clinicPetParentId });
  };

  const clinicPetParent = widgetRequestData?.clinicWidgetRequest?.clinicPetParent;
  const isVerified = widgetRequestData?.clinicWidgetRequest?.isVerified;
  const primaryPhoneNumber = selectPrimaryPhoneNumberBestGuess(clinicPetParent?.phoneNumbers || []);

  const isPetPortalRequest = useMemo<boolean>(
    () => attachment?.attachmentType === ChannelMessageAttachmentType.PetParentRequest,
    [attachment?.attachmentType],
  );

  const task = widgetRequestData?.clinicWidgetRequest?.tasks?.[0];

  const { renderTaskStatusSelector } = useTaskVisualizer({
    taskId: task?.id,
    canRevertComplete: true,
  });

  return (
    <VStack
      data-testid="widget-request-message-container"
      className="WidgetRequest__Message-Container"
      align="flex-start"
      justify="flex-start"
      width="100%"
    >
      <HStack className="WidgetRequest__Header-Container" width="100%" justify="space-between">
        <HStack>
          <Box className="WidgetRequest__Header-Avatar">
            <ClinicAvatar isAutomated />
          </Box>
          <VStack className="WidgetRequest__Header-Text" align="flex-start" spacing={0}>
            <HStack className="WidgetRequest__Header-Text--RequestType" align="baseline">
              <Heading size="sm">{isPetPortalRequest ? 'Pet Portal Request' : 'Website Widget Request'}</Heading>
              <Tooltip label={format(new Date(message.createdAt) || '', "cccc',' PP 'at' p")}>
                <Box>
                  <Text as="p" className="WidgetRequest__Header-Text--TimeStamp" size="xs" variant="info">
                    {format(new Date(message.createdAt) || '', 'h:mma')}
                  </Text>
                </Box>
              </Tooltip>
            </HStack>
            <Text
              data-testid="widget-request-message-request-type-name"
              className="WidgetRequest__Header-Text--RequestName"
              variant="info"
              fontWeight="bold"
              size="sm"
            >
              {requestTypeName}
            </Text>
          </VStack>
        </HStack>
        <Box>{renderTaskStatusSelector()}</Box>
      </HStack>
      {isVerificationRequired && widgetRequestData?.clinicWidgetRequest && !isVerified && (
        <Tooltip
          label="The author of this request must verify their identity using the contact method they provided before they can access this conversation and any additional messages you send."
          placement={PopoverPlacement.AutoStart}
        >
          <Box>
            <Alert my={3} hideCloseButton status="warning" description="Awaiting Verification" />
          </Box>
        </Tooltip>
      )}
      {clinicPetParent && (
        <VStack py={4} spacing={1} className="WidgetRequest__Author-Container" align="flex-start">
          <Text size="sm">Submitted by:</Text>
          <HStack>
            <Box className="WidgetRequest__Author-Avatar">
              <PetParentAvatar clinicPetParent={clinicPetParent} />
            </Box>
            <VStack className="WidgetRequest__Author-Text" ml={2} spacing={0} align="flex-start">
              <Text fontWeight="bold">{[clinicPetParent.firstName, clinicPetParent.lastName].join(' ').trim()}</Text>
              <Link size="sm" onClick={partial(handleViewProfileClick, clinicPetParent.id)}>
                View Profile
              </Link>
            </VStack>
          </HStack>
        </VStack>
      )}
      <WidgetRequestAnswerSummary widgetRequestData={widgetRequestData} primaryPhoneNumber={primaryPhoneNumber} />
    </VStack>
  );
};

export default WidgetRequestMessage;
